import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  Fragment,
} from "react";
import { getStateDescriptions } from "../../utils/things/PRECISION";
import axios from "../../utils/axiosInstance";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";

function getRowBg(state, sd, ctrlType, rowIndex) {
  const stateNum = Number(state);
  const sdNum = Number(sd);
  const isShutdown = ctrlType === "WellPro II" ? stateNum === 12 : stateNum === 130;

  // if (stateNum === -1)
  //   return "bg-orange-950 hover:bg-orange-900";                                          // MODBUS ERROR

  // if (isShutdown && sdNum > 0)
  //   return "bg-red-950 hover:bg-red-900";                                                // shutdown + active SD

  if (isShutdown && sdNum === 0)
    return "bg-gradient-to-r from-red-800 to-yellow-800 animate-pulse";                 // shutdown, SD just cleared

  if (!isShutdown && sdNum > 0)
    return "bg-yellow-800 animate-pulse hover:bg-yellow-800";                           // running but SD warning

  // normal — keep the existing alternating rows
  return rowIndex % 2 === 0
    ? "bg-slate-800 hover:bg-slate-700"
    : "bg-slate-900 hover:bg-slate-700";
}

const LOCKED_REGISTERS = ["STATE", "SHUTDOWN_ENUM"];

const FIXED_HEADERS = [
  "Time",
  "State",
  "SD Enum",
  "SD Code",
  "RPM",
  "Field Pres",
  "Suction Pres",
  "Interstage Pres",
  "Discharge Pres",
  "Eng Oil Temp",
  "Eng Coolant Temp",
  "Eng Manifold Pres",
  "Eng Oil Pres",
  "Dis 1 Temp",
  "Dis 2 Temp",

  "Compressor Oil Pres",
  "System Volts",
  "Main Flow Meter",
  "Eng Hourmeter",
];

const HEADER_TO_REGISTER = {
  "Time": "TIME",
  "State": "STATE",
  "SD Enum": "SHUTDOWN_ENUM",
  "SD Code": "SHUTDOWN_ENUM",
  "RPM": "ENGINE_SPEED",
  "Field Pres": "FIELD_PRES",
  "Suction Pres": "SUCTION_PRES",
  "Interstage Pres": "INTERSTAGE_PRES",
  "Discharge Pres": "DISCHARGE_PRES",
  "Eng Oil Temp": "ENGINE_OIL_TEMP",
  "Eng Coolant Temp": "ENGINE_COOLANT_TEMP",
  "Eng Manifold Pres": "ENGINE_MANIFOLD_PRES",
  "Eng Oil Pres": "ENGINE_OIL_PRES",
  "Dis 2 Temp": "DISCHARGE_2_TEMP",
  "Dis 1 Temp": "DISCHARGE_TEMP_FINAL",
  "Compressor Oil Pres": "COMP_OIL_PRES",
  "System Volts": "SYSTEM_VOTLS",
  "Main Flow Meter": "FLOW_METER",
};

const ALL_REGISTERS = [
  "TIME",
  "STATE",
  "SHUTDOWN_ENUM",
  "ENGINE_SPEED",
  "FIELD_PRES",
  "SUCTION_PRES",
  "INTERSTAGE_PRES",
  "DISCHARGE_PRES",
  "ENGINE_OIL_TEMP",
  "ENGINE_COOLANT_TEMP",
  "ENGINE_MANIFOLD_PRES",
  "ENGINE_OIL_PRES",
  "DISCHARGE_2_TEMP",
  "DISCHARGE_TEMP_FINAL",
  "COMP_OIL_PRES",
  "SYSTEM_VOTLS",
  "FLOW_METER",
];

const PAGE_SIZE = 100; // rows loaded into DOM at a time

export default function DataTable({ device }) {
  const [expandedRow, setExpandedRow] = useState(null);
  const [tableData, settableData] = useState(null);
  const [timeRange, setTimeRange] = useState({ type: "preset", hours: 168 });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "Time",
    direction: "desc",
  });
  const [mode, setMode] = useState("preset");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [sdsData, setSdsData] = useState([]);
  const [user, setUser] = useState(null);
  const [registers, setRegisters] = useState(null);
  const [showSelectPanel, setShowSelectPanel] = useState(false);
  const [selectedRegisters, setSelectedRegisters] = useState([LOCKED_REGISTERS]);
  const [registersReady, setRegistersReady] = useState(false);

  const handlePresetChange = (e) => {
    const hours = Number(e.target.value);
    setTimeRange({ type: "preset", hours });
  };

  const formatRegisterLabel = (reg) => reg.replaceAll("_", " ");

  const headers = useMemo(() => {
    return FIXED_HEADERS.filter((header) => {
      if (header === "Time") return true;
      const reg = HEADER_TO_REGISTER[header];
      return selectedRegisters.includes(reg);
    });
  }, [selectedRegisters]);

  const REGISTER_LABELS = {
    SHUTDOWN_ENUM: "Shutdown Enum",
    ENGINE_SPEED: "Engine Speed",
    FIELD_PRES: "Field Pressure",
    SUCTION_PRES: "Suction Scrubber Pressure",
    INTERSTAGE_PRES: "Interstage Pressure",
    DISCHARGE_PRES: "Discharge Pressure",
    ENGINE_OIL_TEMP: "Engine Oil Temp",
    ENGINE_COOLANT_TEMP: "Engine Coolant Temp",
    ENGINE_MANIFOLD_PRES: "Engine Manifold Pressure",
    ENGINE_OIL_PRES: "Engine Oil Press",
    DISCHARGE_2_TEMP: "2nd Stage Discharge Temp",
    DISCHARGE_TEMP_FINAL: "Discharge Temperature",
    STATE: "State",
    SYSTEM_VOTLS: "System Volts",
    COMP_OIL_PRES: "Compressor Oil Pressure",
    FLOW_METER: "Main Flow Meter",
  };

  useEffect(() => {
    const userString = localStorage.getItem("user");
    if (userString) {
      setUser(JSON.parse(userString));
    } else {
      setRegistersReady(true);
    }
  }, []);

  // Fetch SDS data on component mount
  useEffect(() => {
    const fetchSDSData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/eventParties`,
          {
            params: { companyId: process.env.REACT_APP_COMPANY_ID },
          }
        );
        setSdsData(response.data);
      } catch (error) {
        console.error("Failed to fetch SDS data:", error);
        setSdsData([]);
      }
    };
    fetchSDSData();
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetchUser = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/users/${user.id}`,
        );
        const serverUser = res.data;
        console.log('serverUser', serverUser);
        const tableFields = serverUser.dataTableFields;
        console.log(tableFields, "tableFields");
        const parsedFields =
          typeof tableFields === "string"
            ? JSON.parse(tableFields)
            : tableFields || [];
        setRegisters(parsedFields);
        console.log("Fetched registers:", parsedFields);
      } catch (err) {
        console.error("Error fetching user data table fields fields:", err);
        setRegisters([]);
      }
    };
    fetchUser();
  }, [user]);

  useEffect(() => {
    // if (!registers.length) return;
    if (registers === null) return;
    setSelectedRegisters((prev) => {
      const merged = new Set([...prev, ...LOCKED_REGISTERS, ...registers]);
      return Array.from(merged);
    });
    setRegistersReady(true);
  }, [registers]);

  const handleApplyCustom = () => {
    if (!startDate || !endDate) {
      toast.error("Please select start and end dates");
      return;
    }
    const startOfDay = new Date(startDate);
    startOfDay.setHours(0, 0, 0, 0);
    const start = Math.floor(startOfDay.getTime() / 1000);
    const endOfDay = new Date(endDate);
    endOfDay.setHours(23, 59, 59, 999);
    const end = Math.floor(endOfDay.getTime() / 1000);
    if (start >= end) {
      toast.error("Start date must be before end date");
      return;
    }
    setTimeRange({ type: "custom", start, end });
  };

  const fetchtableData = useCallback(
    async (deviceId) => {
      if (!deviceId || !timeRange || !selectedRegisters.length) return;
      try {
        setLoading(true);
        let start;
        let end;
        if (timeRange.type === "custom") {
          start = timeRange.start;
          end = timeRange.end;
        } else {
          end = Math.floor(Date.now() / 1000);
          start = end - timeRange.hours * 60 * 60;
        }
        const url = `${process.env.REACT_APP_API_GATEAWAY}/iot/chart-api/${deviceId}/${start}/${end}`;
        const body = selectedRegisters;
        const response = await axios.post(url, body);
        const apiData = response.data?.data?.response;
        if (!apiData || !apiData.time) throw new Error("Invalid API response");
        settableData(apiData);
      } catch (e) {
        console.error("❌ Failed to fetch chart data:", e);
        settableData(null);
      } finally {
        setLoading(false);
      }
    },
    [timeRange, selectedRegisters],
  );

  useEffect(() => {
    if (mode === "preset") {
      setTimeRange({ type: "preset", hours: 168 });
    }
  }, [mode]);

  useEffect(() => {
    if (device?.id && registersReady) fetchtableData(device.id);
  }, [device?.id, fetchtableData, registersReady]);

  const fetchEngineData = useCallback(async (deviceId) => {
    if (!deviceId) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/engine-data/${deviceId}`,
      );
      setData(response.data);
    } catch (err) {
      console.error(err);
      setData(null);
    }
  }, []);

  useEffect(() => {
    if (device?.id) fetchEngineData(device.id);
  }, [device, fetchEngineData]);

  // Fixed getRows function - removed the "cosn" typo and added proper dependencies
  const getRows = useCallback((sdsDataParam) => {
    if (!tableData?.time?.length) return [];
    return tableData.time.map((_, i) => {
      const { stateDescr, shutdownDescr } = getStateDescriptions(
        data?.response?.CTRL_TYPE,
        tableData.STATE?.[i],
        tableData.SHUTDOWN_ENUM?.[i],
        sdsDataParam
      );
      const row = {
        __time: tableData.time[i],
        __state: tableData.STATE?.[i],        // ← add this
        __sd: tableData.SHUTDOWN_ENUM?.[i],   // ← add this
        Time: new Date(tableData.time[i] * 1000).toLocaleString(),
      };
      headers.forEach((header) => {
        if (header === "Time") return;
        if (header === "State") {
          row[header] = stateDescr;
          return;
        }
        if (header === "SD Enum") {
          row[header] = shutdownDescr ?? "-";
          return;
        }
        if (header === "SD Code") {
          row[header] = tableData.SHUTDOWN_ENUM?.[i] ?? "-";
          return;
        }
        const reg = HEADER_TO_REGISTER[header];
        row[header] = tableData[reg]?.[i] ?? "-";
      });
      return row;
    });
  }, [tableData, data, headers]);

  const handleSort = (key, direction) => setSortConfig({ key, direction });

  // Fixed filteredRows - now passing sdsData to getRows
  const filteredRows = useMemo(() => {
    let rows = getRows(sdsData).filter((row) =>
      Object.values(row).some((val) =>
        String(val).toLowerCase().includes(searchText.toLowerCase()),
      ),
    );
    if (sortConfig.key) {
      rows.sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];
        if (sortConfig.key === "Time") {
          valA = a.__time;
          valB = b.__time;
        }
        if (valA == null) return 1;
        if (valB == null) return -1;
        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return rows;
  }, [tableData, searchText, sortConfig, sdsData, getRows]);

  // reset visible count when data changes
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [filteredRows]);

  const visibleRows = useMemo(
    () => filteredRows.slice(0, visibleCount),
    [filteredRows, visibleCount],
  );

  // Export handlers
  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `data_table_${device?.id || "device"}.xlsx`);
  };

  const handleDownloadCSV = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredRows);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `data_table_${device?.id || "device"}.csv`);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.text(`Data Table For Device : ${device?.id}`, 14, 10);
    autoTable(doc, {
      head: [headers],
      body: filteredRows.map((row) => headers.map((h) => row[h])),
      startY: 20,
      styles: { fontSize: 6 },
    });
    doc.save(`data_table_${device?.id || "device"}.pdf`);
  };

  const updateUserDataTableFields = async (registers) => {
    if (!user) return;
    try {
      let body = {};
      body = { dataTableFields: JSON.stringify(registers) };
      await axios.put(`${process.env.REACT_APP_API_GATEAWAY}/users/${user.id}`, body);
      toast.success("Data table fields updated!");
    } catch (error) {
      toast.error("Failed to update data table fields ");
      console.error(error);
    }
  };

  // ── drag-to-scroll & top scrollbar sync ──────────────────────────────────
  const tableScrollRef = useRef(null);
  const topScrollRef = useRef(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const [needsScroll, setNeedsScroll] = useState(false);

  const handleTableScroll = () => {
    if (topScrollRef.current && tableScrollRef.current) {
      topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
  };
  const handleTopScroll = () => {
    if (tableScrollRef.current && topScrollRef.current) {
      tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };

  const [tableInnerWidth, setTableInnerWidth] = useState(0);
  useEffect(() => {
    if (!tableScrollRef.current) return;
    const checkScroll = () => {
      const el = tableScrollRef.current;
      setTableInnerWidth(el.scrollWidth);
      setNeedsScroll(el.scrollWidth > el.clientWidth);
    };
    const obs = new ResizeObserver(checkScroll);
    obs.observe(tableScrollRef.current);
    return () => obs.disconnect();
  }, []);

  const onMouseDown = (e) => {
    isDragging.current = true;
    dragStartX.current = e.pageX - tableScrollRef.current.offsetLeft;
    dragScrollLeft.current = tableScrollRef.current.scrollLeft;
    tableScrollRef.current.style.cursor = "grabbing";
    tableScrollRef.current.style.userSelect = "none";
  };
  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    const x = e.pageX - tableScrollRef.current.offsetLeft;
    const walk = x - dragStartX.current;
    tableScrollRef.current.scrollLeft = dragScrollLeft.current - walk;
  };
  const onMouseUp = () => {
    isDragging.current = false;
    tableScrollRef.current.style.cursor = "grab";
    tableScrollRef.current.style.userSelect = "";
  };

  // load more rows when user scrolls near the bottom of the table wrapper
  const tableBodyWrapRef = useRef(null);
  const onTableBodyScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
      setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filteredRows.length));
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        .dt-scroll::-webkit-scrollbar { height: 5px; }
        .dt-scroll::-webkit-scrollbar-track { background: #0f172a; }
        .dt-scroll::-webkit-scrollbar-thumb { background: #334155; border-radius: 99px; }
        .dt-scroll::-webkit-scrollbar-thumb:hover { background: #475569; }

        .dt-top-scroll::-webkit-scrollbar { height: 6px; }
        .dt-top-scroll::-webkit-scrollbar-track { background: #0f172a; border-radius: 99px; }
        .dt-top-scroll::-webkit-scrollbar-thumb { background: #3b82f6; border-radius: 99px; }
        .dt-top-scroll::-webkit-scrollbar-thumb:hover { background: #60a5fa; }

        .dt-body-scroll::-webkit-scrollbar { width: 4px; }
        .dt-body-scroll::-webkit-scrollbar-track { background: #0f172a; }
        .dt-body-scroll::-webkit-scrollbar-thumb { background: #334155; border-radius: 99px; }
        .dt-body-scroll::-webkit-scrollbar-thumb:hover { background: #475569; }
      `}</style>

      <div className="bg-slate-800 rounded-md overflow-hidden">
        {showSelectPanel && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-slate-900 w-full max-w-md rounded-lg p-4">
              <h2 className="text-lg font-semibold text-white mb-3">
                Select Registers
              </h2>
              <div className="max-h-70 overflow-y-auto space-y-2">
                {ALL_REGISTERS.map((reg) => {
                  const isLocked = LOCKED_REGISTERS.includes(reg);
                  return (
                    <label
                      key={reg}
                      className={`flex items-center gap-2 text-sm ${isLocked ? "text-slate-500" : "text-slate-300"}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRegisters.includes(reg)}
                        disabled={isLocked}
                        onChange={() => {
                          if (isLocked) return;
                          setSelectedRegisters((prev) =>
                            prev.includes(reg)
                              ? prev.filter((r) => r !== reg)
                              : [...prev, reg],
                          );
                        }}
                      />
                      {REGISTER_LABELS[reg] || reg.replaceAll("_", " ")}
                    </label>
                  );
                })}
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setShowSelectPanel(false)} className="px-3 py-1 bg-slate-700 text-white rounded">Cancel</button>
                <button
                  onClick={() => { setShowSelectPanel(false); updateUserDataTableFields(selectedRegisters); }}
                  className="px-3 py-1 bg-green-600 text-white rounded"
                >Apply</button>
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="p-3 bg-slate-900 flex flex-wrap gap-3 items-center">
          <input
            type="text"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="flex-1 min-w-[120px] sm:w-48 sm:flex-none px-2 py-1 bg-slate-800 border border-slate-600 text-white text-sm rounded"
          />
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="bg-slate-800 text-white text-sm border border-slate-600 rounded px-2 py-1">
            <option value="preset">Preset</option>
            <option value="custom">Custom</option>
          </select>
          {mode === "preset" && (
            <select className="bg-slate-800 text-white text-sm border border-slate-600 rounded px-2 py-1" value={timeRange.hours} onChange={handlePresetChange}>
              <option value={4}>Last 4 hours</option>
              <option value={8}>Last 8 hours</option>
              <option value={24}>Last 24 hours</option>
              <option value={168}>Last week</option>
              <option value={336}>Last 2 weeks</option>
            </select>
          )}
          {mode === "custom" && (
            <>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-slate-800 text-white text-sm border border-slate-600 rounded px-2 py-1" />
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-slate-800 text-white text-sm border border-slate-600 rounded px-2 py-1" />
              <button onClick={handleApplyCustom} className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded">Apply</button>
            </>
          )}
          <button onClick={() => setShowSelectPanel(true)} className="flex-1 min-w-[100px] sm:flex-none px-3 py-1 rounded bg-green-600 hover:bg-green-700 text-white lg:h-[29px] font-medium">
            <div className="text-[13px] sm:text-base lg:mt-[-2px]">Select Data</div>
          </button>
          <div className="hidden sm:flex sm:flex-1" />
          <div className="flex flex-wrap gap-2 w-full sm:w-auto justify-start sm:justify-end">
            <button onClick={handleDownloadPDF} className="flex-1 sm:flex-none px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 lg:h-7">PDF</button>
            <button onClick={handleDownloadExcel} className="flex-1 sm:flex-none px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">Excel</button>
            <button onClick={handleDownloadCSV} className="flex-1 sm:flex-none px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">CSV</button>
          </div>
        </div>

        {/* ── TOP SCROLLBAR ── */}
        <div
          ref={topScrollRef}
          onScroll={handleTopScroll}
          className="dt-top-scroll overflow-x-auto bg-slate-900 border-b border-slate-700"
          style={{ height: 14 }}
        >
          <div style={{ width: tableInnerWidth, height: 1 }} />
        </div>

        {/* ── scroll hint label ── */}
        {needsScroll && (
          <div className="flex items-center justify-center gap-2 py-1 bg-slate-900 text-slate-500 text-xs select-none">
            <span>◀</span>
            <span>scroll or drag to see more columns</span>
            <span>▶</span>
          </div>
        )}

        {/* ── horizontal scroll wrapper (unchanged) ── */}
        <div
          ref={tableScrollRef}
          onScroll={handleTableScroll}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          className="dt-scroll overflow-x-auto"
          style={{ cursor: "grab" }}
        >
          {/* ── vertical scroll wrapper: sits OUTSIDE the table, limits DOM height ── */}
          <div
            ref={tableBodyWrapRef}
            onScroll={onTableBodyScroll}
            className="dt-body-scroll overflow-y-auto"
            style={{ maxHeight: "67vh" }}
          >
            <table className="w-full text-sm">
              <thead className="bg-slate-900 border-b border-slate-700 sticky top-0 z-10">
                <tr>
                  {/* Mobile arrow column */}
                  <th className="px-3 py-2 lg:hidden w-8"></th>
                  {headers.map((header) => (
                    <th
                      key={header}
                      onClick={() =>
                        setSortConfig((prev) => ({
                          key: header,
                          direction: prev.key === header && prev.direction === "asc" ? "desc" : "asc",
                        }))
                      }
                      className={`px-3 py-2.5 text-center text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer select-none
                      ${header !== "State" && header !== "SD Enum" ? "hidden sm:table-cell" : ""}`}
                    >
                      {header}
                      {sortConfig.key === header ? (sortConfig.direction === "asc" ? "▲" : "▼") : "↕"}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* tbody is 100% original — zero changes inside */}
              <tbody className="divide-y divide-slate-700">
                {loading ? (
                  <tr>
                    <td colSpan={headers.length + 1} className="text-center text-slate-400 py-4">Loading...</td>
                  </tr>
                ) : visibleRows.length ? (
                  <>
                    {visibleRows.map((row, i) => {
                      const isOpen = expandedRow === i;
                      return (
                        <Fragment key={i}>
                          {/* MAIN ROW — identical to original */}
                          <tr className={`text-gray-200 ${getRowBg(row.__state, row.__sd, data?.response?.CTRL_TYPE, i)}`}>
                            <td className="px-3 py-2 lg:hidden text-center">
                              <button onClick={() => setExpandedRow(isOpen ? null : i)} className="text-slate-300">
                                {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                              </button>
                            </td>
                            {headers.map((h, j) => (
                              <td key={j} className={`px-3 py-2 text-center text-slate-300 ${h !== "State" && h !== "SD Enum" ? "hidden sm:table-cell" : ""}`}>
                                {h === "SD Enum" ? (
                                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-slate-700 text-slate-200 border border-slate-600">
                                    {row[h] ?? "-"}
                                  </span>
                                ) : (row[h] ?? "-")}
                              </td>
                            ))}
                          </tr>

                          {/* EXPANDED CONTENT (mobile only) — identical to original */}
                          {isOpen && (
                            <tr className="lg:hidden bg-slate-900">
                              <td colSpan={headers.length + 1} className="px-4 py-3 text-sm text-slate-300">
                                <div className="space-y-2">
                                  {headers.filter((h) => h !== "State" && h !== "SD Enum").map((h) => (
                                    <div key={h}>
                                      <span className="text-slate-400">{h}:</span> {row[h] ?? "-"}
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}

                    {/* load-more indicator */}
                    {visibleCount < filteredRows.length && (
                      <tr>
                        <td colSpan={headers.length + 1} className="text-center text-slate-500 py-3 text-xs">
                          Scroll down to load more rows…
                        </td>
                      </tr>
                    )}
                  </>
                ) : (
                  <tr>
                    <td colSpan={headers.length + 1} className="text-center text-slate-400 py-4">No data found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="px-3 py-2 bg-slate-900 border-t border-slate-700 text-xs text-slate-400">
          Showing {filteredRows.length} / {tableData?.time?.length ?? 0} data points
        </div>
      </div>
    </>
  );
}